import React from "react";
import { format } from "date-fns";
import { Link } from "react-router-dom";

import { Paragraph, Span, H2 } from "../Typography/Typography";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";
import { Button } from "../Button/Button";
import { Container } from "../Container/Container";
import { EmptyStateBox } from "../EmptyStateBox/EmptyStateBox";
import { useProfile } from "../../contexts/ProfileContext";
import { markAlbumAsListened, albumPath, Recommendation } from "../../api/albums";
import { ClerkUser } from "../../api/clerk";

import "./TodaysAlbum.scss";

type GroupedRecommendation = {
    album_id: number;
    title: string;
    artist_name: string;
    source_cover_url: string | null;
    recommendations: Recommendation[];
};

function groupByAlbum(recommendations: Recommendation[]): GroupedRecommendation[] {
    const groups = new Map<number, GroupedRecommendation>();

    for (const rec of recommendations) {
        const existing = groups.get(rec.album_id);

        if (existing) {
            existing.recommendations.push(rec);
            continue;
        }

        groups.set(rec.album_id, {
            album_id: rec.album_id,
            title: rec.title,
            artist_name: rec.artist_name,
            source_cover_url: rec.source_cover_url,
            recommendations: [rec],
        });
    }

    return Array.from(groups.values());
}

function formatRecommenderNames(
    recommendations: Recommendation[],
    usersByExternalId: Record<string, ClerkUser>
) {
    const names = recommendations
        .map((rec) => {
            const user = usersByExternalId[String(rec.from_user_id)];
            return user?.username ? `@${user.username}` : null;
        })
        .filter(Boolean);

    if (names.length === 0) {
        return "From friends";
    }

    if (names.length === 1) {
        return `From ${names[0]}`;
    }

    if (names.length === 2) {
        return `From ${names[0]} and ${names[1]}`;
    }

    return `From ${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

function getUniqueRecommenders(
    recommendations: Recommendation[],
    usersByExternalId: Record<string, ClerkUser>
) {
    const seen = new Set<string>();
    const recommenders: ClerkUser[] = [];

    for (const rec of recommendations) {
        const id = String(rec.from_user_id);
        if (seen.has(id)) {
            continue;
        }

        seen.add(id);
        const user = usersByExternalId[id];
        if (user) {
            recommenders.push(user);
        }
    }

    return recommenders;
}

function RecommenderAttribution({
    recommendations,
    usersByExternalId,
}: {
    recommendations: Recommendation[];
    usersByExternalId: Record<string, ClerkUser>;
}) {
    const recommenders = getUniqueRecommenders(recommendations, usersByExternalId);
    const recommenderText = formatRecommenderNames(recommendations, usersByExternalId);

    return (
        <div className="todays-album__recommenders">
            {recommenders.length > 0 && (
                <div className="todays-album__recommender-avatars" aria-hidden>
                    {recommenders.map((user) => (
                        <img key={user.id} src={user.imageUrl} alt="" width={24} height={24} />
                    ))}
                </div>
            )}
            <Span variant="body-4" color="mid-contrast">
                {recommenderText}
            </Span>
        </div>
    );
}

function AlbumCover({ coverUrl, title }: { coverUrl: string | null; title: string }) {
    if (coverUrl) {
        return <img className="todays-album__cover" src={coverUrl} alt="" />;
    }

    return <div className="todays-album__cover todays-album__cover--placeholder" aria-hidden />;
}

function TodaysAlbumCard({
    isCurrentUser,
    album,
    recommendations,
    usersByExternalId,
    isListened,
    isMarking,
    markError,
    onMarkAsListened,
}: {
    isCurrentUser: boolean;
    album: GroupedRecommendation;
    recommendations: Recommendation[];
    usersByExternalId: Record<string, ClerkUser>;
    isListened: boolean;
    isMarking: boolean;
    markError: string | null;
    onMarkAsListened?: () => void;
}) {

    let actionToRender = null;
    if (isCurrentUser) {
        if (isListened) {
            actionToRender = (
                <span className="todays-album__listened-check" aria-label="Listened today">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                        <path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                    </svg>
                </span>
            );
        } else if (onMarkAsListened) {
            actionToRender = (
                <div className="todays-album__actions">
                    <Button
                        type="button"
                        variant="solid-brand"
                        disabled={isMarking}
                        onClick={onMarkAsListened}
                    >
                        {isMarking ? "Saving…" : "Mark as listened"}
                    </Button>
                    {markError && (
                        <Paragraph
                            className="todays-album__error"
                            variant="body-4"
                            color="mid-contrast"
                        >
                            {markError}
                        </Paragraph>
                    )}
                </div>
            );
        }

       
    }

    return (
        <Container as="article" className="todays-album">
            <Link className="todays-album__link" to={albumPath(album.album_id)}>
                <AlbumCover coverUrl={album.source_cover_url} title={album.title} />
                <div className="todays-album__meta">
                    <Span
                        className="todays-album__title-container"
                        variant="body-4"
                        weight="bold"
                        color="brand-high-contrast"
                    >
                        Today&apos;s album
                    </Span>
                    <Span variant="heading-3" weight="bold" fontFamily="sans-serif">
                        {album.title}
                    </Span>
                    <Span variant="body-3" color="mid-contrast">
                        {album.artist_name}
                    </Span>
                </div>
            </Link>
            <div className="todays-album__footer">
                <RecommenderAttribution
                    recommendations={recommendations}
                    usersByExternalId={usersByExternalId}
                />
                {actionToRender}
            </div>
        </Container>
    );
}

function axiosErrorMessage(error: unknown) {
    if (
        typeof error === "object" &&
        error !== null &&
        "response" in error &&
        typeof error.response === "object" &&
        error.response !== null &&
        "data" in error.response &&
        typeof error.response.data === "object" &&
        error.response.data !== null &&
        "error" in error.response.data &&
        typeof error.response.data.error === "string"
    ) {
        return error.response.data.error;
    }

    return null;
}

export function TodaysAlbum() {
    const {
        isCurrentUser,
        listenTabData,
        queueRecommendations,
        doneRecommendations,
        usersByExternalId,
        isLoadingListenTab,
        refreshListenTab,
    } = useProfile();

    const [isMarkingListened, setIsMarkingListened] = React.useState(false);
    const [markListenedError, setMarkListenedError] = React.useState<string | null>(null);

    const showSection = isCurrentUser || Boolean(listenTabData.listened_today);

    if (!showSection) {
        return null;
    }

    const todaysAlbumSource = isCurrentUser
        ? listenTabData.listened_today ?? listenTabData.queue_next
        : listenTabData.listened_today;
    const todayKey = format(new Date(), "yyyy-MM-dd");

    const todaysAlbumRecommendations = listenTabData.listened_today
        ? doneRecommendations.filter(
              (rec) =>
                  format(new Date(rec.updated_on), "yyyy-MM-dd") === todayKey &&
                  Number(rec.album_id) === Number(listenTabData.listened_today!.album_id)
          )
        : queueRecommendations.filter(
              (rec) =>
                  listenTabData.queue_next &&
                  Number(rec.album_id) === Number(listenTabData.queue_next.album_id)
          );

    const recommendationsForGroup =
        todaysAlbumRecommendations.length > 0
            ? todaysAlbumRecommendations
            : todaysAlbumSource
              ? [todaysAlbumSource]
              : [];

    const todaysAlbumGroup = todaysAlbumSource
        ? groupByAlbum(recommendationsForGroup)[0]
        : null;

    const handleMarkAsListened = async (albumId: number) => {
        setIsMarkingListened(true);
        setMarkListenedError(null);

        try {
            await markAlbumAsListened(albumId);
            await refreshListenTab({ showLoading: false });
        } catch (error) {
            console.error("Error marking album as listened:", error);
            setMarkListenedError(
                axiosErrorMessage(error) ?? "Could not mark album as listened. Please try again."
            );
        } finally {
            setIsMarkingListened(false);
        }
    };

    return (
        <div className="todays-album-section">
            {isLoadingListenTab ? (
                <LoadingSpinner />
            ) : todaysAlbumGroup ? (
                <TodaysAlbumCard
                    isCurrentUser={isCurrentUser}
                    album={todaysAlbumGroup}
                    recommendations={recommendationsForGroup}
                    usersByExternalId={usersByExternalId}
                    isListened={Boolean(listenTabData.listened_today)}
                    isMarking={isMarkingListened}
                    markError={markListenedError}
                    onMarkAsListened={
                        isCurrentUser && !listenTabData.listened_today
                            ? () => handleMarkAsListened(todaysAlbumGroup.album_id)
                            : undefined
                    }
                />
            ) : (
                <EmptyStateBox>
                    No albums waiting. Friends can recommend albums to you here.
                </EmptyStateBox>
            )}
        </div>
    );
}
